/**
 * Preparing a photograph for the evidence bucket.
 *
 * A phone camera hands over 4–12 MB per shot. Over the quarry's signal that
 * is minutes per photo and a tablet full of blobs while it waits, so every
 * image is downscaled and re-encoded before it ever reaches the outbox:
 * longest edge 1600 px, JPEG quality 0.8 (docs/04-offline-sync.md §9).
 *
 * Quarry photos are evidence, not portfolio work — 1600 px is enough to read
 * a cracked hose or a ticket number, and small enough to send on a bad line.
 */

export const MAX_EDGE = 1600;
export const JPEG_QUALITY = 0.8;

/** The bucket's own limit; refuse earlier so the failure is legible. */
export const MAX_BYTES = 15 * 1024 * 1024;

export const ACCEPTED = 'image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf';

export interface PreparedFile {
  blob: Blob;
  mime: string;
  width: number | null;
  height: number | null;
  fileName: string;
}

export class FileTooLarge extends Error {}
export class UnsupportedFile extends Error {}

function extensionFor(mime: string): string {
  if (mime === 'application/pdf') return 'pdf';
  if (mime === 'image/png') return 'png';
  if (mime === 'image/webp') return 'webp';
  return 'jpg';
}

/** Where this file lives in the bucket.
 *
 *  The attachment's own id is the file name, so a retried upload overwrites
 *  the same object rather than leaving an orphan next to it. That is what
 *  makes replaying the outbox after a dropped connection safe. */
export function storagePath(
  entityType: string,
  entityId: string,
  attachmentId: string,
  mime: string,
): string {
  return `${entityType}/${entityId}/${attachmentId}.${extensionFor(mime)}`;
}

async function decode(file: File): Promise<ImageBitmap | HTMLImageElement> {
  // createImageBitmap applies the EXIF orientation, which is what stops a
  // photo taken in portrait arriving on its side.
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch {
      /* Safari < 17 and some Android webviews: fall through. */
    }
  }
  const url = URL.createObjectURL(file);
  try {
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new UnsupportedFile('That image could not be read.'));
      img.src = url;
    });
  } finally {
    // Revoked on the next tick so the decode has finished with it.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}

/**
 * Downscale and re-encode. PDFs and anything already small enough pass
 * through untouched — re-encoding a 200 KB ticket scan gains nothing and
 * loses detail.
 */
export async function prepareFile(file: File): Promise<PreparedFile> {
  const mime = file.type || 'application/octet-stream';

  if (mime === 'application/pdf') {
    if (file.size > MAX_BYTES) {
      throw new FileTooLarge(
        `That PDF is ${(file.size / 1024 / 1024).toFixed(1)} MB. The limit is 15 MB.`,
      );
    }
    return { blob: file, mime, width: null, height: null, fileName: file.name };
  }

  if (!mime.startsWith('image/')) {
    throw new UnsupportedFile('Only photographs and PDFs can be attached.');
  }

  const source = await decode(file);
  const sw = 'width' in source ? source.width : 0;
  const sh = 'height' in source ? source.height : 0;
  if (!sw || !sh) throw new UnsupportedFile('That image could not be read.');

  const scale = Math.min(1, MAX_EDGE / Math.max(sw, sh));
  const width = Math.round(sw * scale);
  const height = Math.round(sh * scale);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new UnsupportedFile('This device cannot process images.');
  // A white ground: a PNG with transparency would otherwise go black when
  // flattened into JPEG.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(source as CanvasImageSource, 0, 0, width, height);
  if ('close' in source) source.close();

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY));
  if (!blob) throw new UnsupportedFile('That image could not be converted.');

  if (blob.size > MAX_BYTES) {
    throw new FileTooLarge('That image is still too large after compression.');
  }

  return {
    blob,
    mime: 'image/jpeg',
    width,
    height,
    fileName: file.name.replace(/\.[^.]+$/, '') + '.jpg',
  };
}

/** Human-readable size, in the app's number format. */
export function fileSize(bytes: number | null | undefined): string {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0).replace('.', ',')} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1).replace('.', ',')} MB`;
}
